import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import SEOPageLayout from "@/components/SEOPageLayout";
import { CONFERENCE_KEYWORDS, joinKw } from "@/config/keywords";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_custom-team-jerseys-1/artifacts/czetrdap_Youth%20ministry%20polo%20shirt%20uniform.png";

export default function ChurchConferenceUniforms() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Church Conference Uniforms",
    serviceType: "Custom church conference uniform design and manufacturing",
    provider: {
      "@type": "LocalBusiness",
      name: "Tribal Designz",
      address: { "@type": "PostalAddress", addressLocality: "Auckland", addressCountry: "NZ" },
      telephone: "+64 22 101 2443",
    },
  };

  const imageObjects = [{
    url: HERO_IMG,
    alt: "Church Conference Polo Shirts — Pacific youth volunteers in custom conference uniforms serving community Auckland NZ",
    name: "Conference Excellence Collection — Volunteer Polos",
    representative: true,
  }];

  return (
    <>
      <SEOHead
        title="Church Conference Uniforms NZ | Polo Shirts in Bulk"
        description="Church Conference Uniforms NZ — custom polo shirts for Pacific church conferences, volunteers, hosts, MCs & speakers. Bulk pricing. Free design mockup in 24h."
        path="/church-conference-uniforms"
        image={HERO_IMG}
        imageAlt="Church Conference Polo Shirts — Pacific custom uniforms Auckland NZ"
        keywords={joinKw(CONFERENCE_KEYWORDS)}
        jsonLd={jsonLd}
        imageObjects={imageObjects}
      />
      <SEOPageLayout
        heroEyebrow="Conference Excellence Collection™"
        heroH1="Church Conference Uniforms — Bulk Polo Shirts for Your Event"
        heroSub="Custom church conference uniforms in NZ. Coordinated polos for organisers, hosts, volunteers, MCs and speakers — your conference dressed for the big stage."
        heroImage={HERO_IMG}
        heroImageAlt="Pacific church conference polo uniforms — Auckland NZ"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Church Conference Uniforms" }]}
      >
        <h2>The polo uniforms that make your conference look intentional</h2>
        <p>
          A Pacific church conference is one of the biggest events on the
          community calendar — Conference Kakai, EFKS autasi, AOG general
          conference, CICC Nuku, district fellowship gatherings. Hundreds of
          people in one venue, multiple sessions, volunteers running every
          corner of the operation. The visual difference between a
          conference where the host team is in matching polos vs. a
          conference where everyone's in their own shirt is enormous —
          and so is the photo legacy of the event.
        </p>
        <p>
          Tribal Designz designs and produces <strong>custom church
          conference uniforms</strong> for Pacific churches across Aotearoa
          — Samoan, Tongan, Cook Islands, Fijian, Niuean and pan-Pacific
          conferences alike. Built for the scale of a big event and the
          tight timeline that always comes with one.
        </p>

        <h2>Who needs a conference polo</h2>
        <ul>
          <li><strong>Conference organising committee</strong></li>
          <li><strong>Volunteer / host teams</strong> — registration, ushers, hospitality, security</li>
          <li><strong>MCs &amp; programme leads</strong></li>
          <li><strong>Guest speakers &amp; international visitors</strong></li>
          <li><strong>Youth volunteer crews</strong></li>
          <li><strong>Catering / kitchen teams</strong></li>
          <li><strong>Tech &amp; AV crew</strong></li>
          <li><strong>Children's programme leaders</strong></li>
        </ul>
        <p>
          We often produce <em>tiered</em> conference polos — same overall
          design but with role-specific colours or chest text (Organiser,
          Volunteer, MC, Speaker) so attendees instantly know who to ask
          for what.
        </p>

        <h2>Design elements built for conferences</h2>
        <ul>
          <li>Conference theme &amp; year embroidery or print</li>
          <li>Host church / district name and crest</li>
          <li>Scripture reference (commonly used for major Pacific church conferences)</li>
          <li>Sponsor logos (with approval)</li>
          <li>Cultural pattern overlay tied to the host community</li>
          <li>Role identifier on the back (e.g. "VOLUNTEER", "HOST", "MC")</li>
        </ul>

        <h2>Lead times — start early</h2>
        <p>
          Big conferences need big lead times. We recommend:
        </p>
        <ul>
          <li><strong>50–100 polos</strong>: 4 weeks from brief to delivery</li>
          <li><strong>100–250 polos</strong>: 5–6 weeks</li>
          <li><strong>250–500 polos</strong>: 6–8 weeks</li>
          <li><strong>500+ polos</strong>: by quote, plan 8+ weeks</li>
        </ul>
        <p>
          We can split deliveries if you need volunteer kits earlier and
          general attendee polos later in the build-up.
        </p>

        <h2>Pricing</h2>
        <p>
          Volume pricing kicks in fast for conferences. From{" "}
          <strong>$54 per polo</strong> at 25–49 units and{" "}
          <strong>$49 per polo</strong> at 50–99. For 100+ units we issue
          custom quotes — typically the per-polo price drops further at
          250+ and 500+ thresholds. See our <a href="/#pricing">pricing
          table</a> for the standard tiers.
        </p>

        <h2>Related</h2>
        <p>
          <Link to="/samoan-church-uniforms">Samoan church uniforms</Link> ·{" "}
          <Link to="/tongan-church-uniforms">Tongan church uniforms</Link> ·{" "}
          <Link to="/cook-islands-uniforms">Cook Islands uniforms</Link> ·{" "}
          <Link to="/church-uniforms-auckland">Church uniforms Auckland</Link>
        </p>
      </SEOPageLayout>
    </>
  );
}
